import { computed, observable, action } from 'mobx';
import GdsGuestModel from '~/glm/models/gds/gds-guest-model';
import GdsModel from '~/glm/models/gds/gds-model';
import GdsApi from '~/glm/utils/gds-api';
import { validMailingAddress, sanitizedAddress } from '~/glm/utils/address-utils';
import { getChangedProps } from '~/glm/utils/analytics';

export default class GdsHouseholdModel extends GdsModel {
  id;
  weddingId;
  createdAt;
  @observable description;
  @observable groupId;
  @observable street1;
  @observable street2;
  @observable city;
  @observable state;
  @observable zip;
  @observable country;
  @observable rsvpNote;
  @observable coupleNote;
  @observable members = [];
  @observable answers;

  constructor(household = {}) {
    super();
    this.id = household.id;
    this.weddingId = household.weddingId;
    this.createdAt = new Date(household.createdAt);
    this.description = household.description;
    this.groupId = household.groupId;
    this.rsvpNote = household.rsvpNote;
    this.coupleNote = household.coupleNote;
    this.answers = household.answers;
    if (household.address) {
      for (var key in household.address) {
        this[key] = household.address[key];
      }
    }
    if (household.people) {
      household.people.forEach(person => this.members.push(new GdsGuestModel(person, this.id)));
    }
  }

  @computed
  get apiEndpoint() {
    return `/v1/households${this.id ? `/${this.id}` : ''}`;
  }

  @computed
  get apiParams() {
    const people = this.members.map(guest => guest.apiParams);
    return {
      weddingId: this.weddingId,
      groupId: this.groupId,
      coupleNote: this.coupleNote,
      people,
      address: sanitizedAddress(this.getAddress),
    };
  }

  static invitationsBulk = '/v1/invitations/bulk';

  @computed
  get leaderPerson() {
    return this.members.find(guest => guest.isLeader);
  }

  @computed
  get count() {
    return this.members.length;
  }

  @computed
  get getAddress() {
    return {
      street1: this.street1,
      street2: this.street2,
      state: this.state,
      city: this.city,
      zip: this.zip,
      country: this.country,
      fullAddress: this.fullAddress,
    };
  }

  @computed
  get getMembers() {
    return this.members.map(a => a);
  }

  @action
  addMember(guest) {
    this.members.push(guest);
  }

  acceptedGuests(eventId) {
    return this.guestInvitations(eventId).filter(invitation => invitation.rsvp === true);
  }

  declinedGuests(eventId) {
    return this.guestInvitations(eventId).filter(invitation => invitation.rsvp === false);
  }

  noResponseGuests(eventId) {
    return this.guestInvitations(eventId).filter(invitation => invitation.rsvp == null);
  }

  guestInvitations(eventId) {
    const invitations = this.members.map(member => member.getInvitationByEvent(eventId));
    return invitations.filter(invitation => invitation);
  }

  invitedGuests(eventId) {
    return this.members.filter(member => member.getInvitationByEvent(eventId));
  }

  anyGuestThankYouSent = eventId => {
    return this.guestInvitations(eventId).some(invitation => invitation.thankYouSent);
  };

  getGift = eventId => {
    const invitations = this.guestInvitations(eventId).find(invite => invite.gift);
    if (invitations) {
      return invitations.gift;
    }

    return undefined;
  };

  updateHouseholdInvites = (invites, update) => {
    return invites.map(invitation => {
      const inviteCopy = { ...invitation, ...update };
      return this.cleanInviteForGds(inviteCopy);
    });
  };

  cleanInviteForGds(invitation) {
    const inviteCopy = { ...invitation };
    delete inviteCopy.guestId;
    delete inviteCopy.householdID;
    delete inviteCopy.dirty;
    return inviteCopy;
  }

  toggleInvitedGuestsThankYouSent(eventId) {
    const value = !this.anyGuestThankYouSent(eventId);
    const invitations = this.guestInvitations(eventId);
    const update = { thankYouSent: value };
    const updatedInvitations = this.updateHouseholdInvites(invitations, update);

    return this.updateInvites(updatedInvitations, eventId);
  }

  @action
  updateInvites = (updatedInvitations, eventId) => {
    const invitations = this.guestInvitations(eventId);
    return GdsApi.put(GdsHouseholdModel.invitationsBulk, updatedInvitations).then(updatedInvites => {
      updatedInvites.forEach(invite => {
        const invitation = invitations.find(invititation => invititation.id === invite.id);
        invitation.updateInvite(invite);
      });
    });
  };

  updateHousehold = (analyticsStore = {}, trackingParams = {}) => {
    this.put(this.apiParams).then(response => {
      const { people } = response;
      const newGuests = people.filter(person => !this.members.find(member => member.id === person.id));
      this.addGuests(newGuests, analyticsStore, trackingParams);
      this.dirtyMembers.forEach(member => {
        member.updated = true; // eslint-disable-line no-param-reassign
        member.dirty = false; // eslint-disable-line no-param-reassign
      });
      this.removeTempGuests();
    });
    return Promise.resolve();
  };

  @action
  removeMember(guest) {
    this.members.remove(guest);
    // sometimes this function is used to remove temp guests not in guestCollection
    if (this.guestCollection.has(guest.id)) {
      this.guestCollection.remove(guest);
    }
  }

  removeTempGuests = () => {
    this.newMembers.forEach(guest => this.removeMember(guest));
  };

  addGuests = (newGuests, analyticsStore, trackingParams) => {
    newGuests.forEach(guest => {
      const { address, email, phone } = this.leaderPerson;
      const newGuest = new GdsGuestModel(guest, this.id);
      const country = newGuest.country || this.leaderPerson.country;
      this.addMember(newGuest);
      const props = getChangedProps({
        guestFirstName: newGuest.firstName,
        guestLastName: newGuest.lastName,
        guestAddress: validMailingAddress(address) || '',
        guestEmail: email,
        guestPhoneNumber: phone,
      });
      Object.assign(trackingParams, props);
      analyticsStore.track('Guest Added', {
        ...trackingParams,
        guestCountryCode: country,
      });
    });
  };

  @computed
  get newMembers() {
    return this.members.filter(member => member.isNew);
  }

  @computed
  get dirtyMembers() {
    return this.members.filter(member => member.dirty);
  }

  @computed
  get contactable() {
    return this.contactableByEmail || this.contactableByPhone;
  }

  @computed
  get contactableByEmail() {
    return this.leaderPerson && this.leaderPerson.email;
  }

  @computed
  get contactableByPhone() {
    return this.leaderPerson && this.leaderPerson.phone;
  }

  @computed
  get hasValidMailingAddress() {
    return this.leaderPerson && this.leaderPerson.hasValidMailingAddress;
  }

  @computed
  get plusOnes() {
    return this.members.filter(member => !member.isLeader);
  }
}
